import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Check,
  CheckCircle2,
  Clock3,
  Copy,
  ExternalLink,
  FileCheck2,
  FolderKanban,
  Link2,
  Maximize2,
  Menu,
  MessageSquareText,
  Paperclip,
  PlayCircle,
  Printer,
  RotateCcw,
  ShieldCheck,
  Sparkles,
  X,
} from "lucide-react";
import SEO from "@/components/SEO";
import {
  CLAUDE_BASICS_STEPS,
  type ClaudeBasicsPrompt,
  type ClaudeBasicsStep,
} from "@/data/claudeBasics";
import { trackClick, trackEvent } from "@/utils/posthog";

const STORAGE_KEY = "claude-basics-completed-steps";
const SLIDE_STORAGE_KEY = "claude-basics-current-slide";

type SlideTone = "dark" | "paper" | "blue" | "orange";
type SlideKind =
  | "cover"
  | "roadmap"
  | "activity"
  | "concept"
  | "prompt"
  | "checkpoint"
  | "finish";

type ConceptType = "prompt-formula" | "feedback-loop" | "artifact" | "project";

interface DeckSlide {
  id: string;
  title: string;
  kind: SlideKind;
  tone: SlideTone;
  eyebrow?: string;
  heading?: string;
  body?: string;
  stepId?: string;
  actionIndexes?: number[];
  promptIndexes?: number[];
  showImage?: boolean;
  showTable?: boolean;
  showWhy?: boolean;
  showNote?: boolean;
  showLinks?: boolean;
  concept?: ConceptType;
  completesStep?: boolean;
  nextLabel?: string;
}

const DECK_SLIDES: DeckSlide[] = [
  {
    id: "welcome",
    title: "Claude Fundamentals",
    kind: "cover",
    tone: "dark",
    nextLabel: "Let’s get started",
  },
  {
    id: "ten-step-roadmap",
    title: "The ten steps",
    kind: "roadmap",
    tone: "dark",
    eyebrow: "Here’s the plan",
    heading: "We’ll build this one useful step at a time.",
    nextLabel: "Let’s set up Claude",
  },
  {
    id: "step-download-desktop",
    title: "First, get Claude on your computer",
    kind: "activity",
    tone: "blue",
    stepId: "download-desktop",
    actionIndexes: [0, 1, 2, 3],
    showImage: true,
    showWhy: true,
    showLinks: true,
    completesStep: true,
    nextLabel: "Claude is ready",
  },
  {
    id: "step-choose-model",
    title: "Get comfortable with the four controls you’ll use most",
    kind: "activity",
    tone: "paper",
    stepId: "choose-model",
    actionIndexes: [0, 1, 2, 3],
    showImage: true,
    showWhy: true,
    nextLabel: "Now pick a model",
  },
  {
    id: "choose-model-table",
    title: "When you’re not sure, start with Sonnet",
    kind: "activity",
    tone: "blue",
    stepId: "choose-model",
    actionIndexes: [4, 5],
    showTable: true,
    showNote: true,
    showLinks: true,
    nextLabel: "Quick check",
  },
  {
    id: "choose-model-checkpoint",
    title: "You know where the work lives and which model to pick",
    kind: "checkpoint",
    tone: "dark",
    stepId: "choose-model",
    completesStep: true,
    nextLabel: "Step 2 is done",
  },
  {
    id: "step-safe-defaults",
    title: "Set the privacy boundaries before the real work starts",
    kind: "activity",
    tone: "paper",
    stepId: "safe-defaults",
    actionIndexes: [0, 1, 2, 3],
    showImage: true,
    showWhy: true,
    nextLabel: "Set your sharing rule",
  },
  {
    id: "privacy-boundary",
    title: "Know what stays out of Claude",
    kind: "checkpoint",
    tone: "orange",
    stepId: "safe-defaults",
    actionIndexes: [4],
    showNote: true,
    showLinks: true,
    completesStep: true,
    nextLabel: "Step 3 is done",
  },
  {
    id: "step-custom-instructions",
    title: "Give Claude the context you’re tired of repeating",
    kind: "activity",
    tone: "paper",
    stepId: "custom-instructions",
    actionIndexes: [0, 1, 2],
    showImage: true,
    showWhy: true,
    nextLabel: "Add the protocol",
  },
  {
    id: "custom-instructions-prompts",
    title: "Add the context once. Let Claude carry it forward",
    kind: "prompt",
    tone: "blue",
    stepId: "custom-instructions",
    actionIndexes: [3, 4],
    promptIndexes: [0, 1],
    showLinks: true,
    nextLabel: "Try a fresh chat",
  },
  {
    id: "custom-instructions-checkpoint",
    title: "A new chat already knows how to help you",
    kind: "checkpoint",
    tone: "dark",
    stepId: "custom-instructions",
    completesStep: true,
    nextLabel: "Step 4 is done",
  },
  {
    id: "step-set-up-connectors",
    title: "Connect one source you already trust",
    kind: "activity",
    tone: "paper",
    stepId: "set-up-connectors",
    actionIndexes: [0, 1, 2, 3],
    showImage: true,
    showWhy: true,
    showNote: true,
    showLinks: true,
    nextLabel: "Check the answer",
  },
  {
    id: "connectors-checkpoint",
    title: "Check Claude’s answer against the source",
    kind: "checkpoint",
    tone: "orange",
    stepId: "set-up-connectors",
    actionIndexes: [4],
    completesStep: true,
    nextLabel: "Step 5 is done",
  },
  {
    id: "step-how-might-we",
    title: "Start with the problem. Don’t make Claude guess",
    kind: "prompt",
    tone: "blue",
    stepId: "how-might-we",
    actionIndexes: [0, 1, 2, 3],
    promptIndexes: [0],
    showNote: true,
    showLinks: true,
    nextLabel: "Run your prompt",
  },
  {
    id: "how-might-we-checkpoint",
    title: "Claude asked the right questions before it answered",
    kind: "checkpoint",
    tone: "dark",
    stepId: "how-might-we",
    actionIndexes: [4],
    completesStep: true,
    nextLabel: "Step 6 is done",
  },
  {
    id: "step-improve-answer",
    title: "The first response is a starting point, not the finish line",
    kind: "concept",
    tone: "dark",
    stepId: "improve-answer",
    concept: "feedback-loop",
    actionIndexes: [0, 1, 2],
    showWhy: true,
    nextLabel: "Coach the next version",
  },
  {
    id: "improve-answer-checkpoint",
    title: "Make the next version better in the same chat",
    kind: "prompt",
    tone: "paper",
    stepId: "improve-answer",
    actionIndexes: [3],
    promptIndexes: [0],
    showNote: true,
    completesStep: true,
    nextLabel: "Step 7 is done",
  },
  {
    id: "step-work-from-file",
    title: "Give Claude the source, not just the assignment",
    kind: "activity",
    tone: "paper",
    stepId: "work-from-file",
    actionIndexes: [0, 1],
    showImage: true,
    showWhy: true,
    nextLabel: "Ask about the file",
  },
  {
    id: "file-review-prompt",
    title: "Ask one clear question about the file",
    kind: "prompt",
    tone: "blue",
    stepId: "work-from-file",
    actionIndexes: [2],
    promptIndexes: [0],
    nextLabel: "Check the answer",
  },
  {
    id: "work-from-file-checkpoint",
    title: "Trust, then verify, against the source",
    kind: "checkpoint",
    tone: "dark",
    stepId: "work-from-file",
    actionIndexes: [3],
    completesStep: true,
    nextLabel: "Step 8 is done",
  },
  {
    id: "step-create-artifact",
    title: "Use the chat to make the work. Put the work in an Artifact",
    kind: "concept",
    tone: "dark",
    stepId: "create-artifact",
    concept: "artifact",
    showWhy: true,
    showImage: true,
    nextLabel: "Make the Artifact",
  },
  {
    id: "artifact-prompt",
    title: "Turn the answer into something you can actually use",
    kind: "prompt",
    tone: "paper",
    stepId: "create-artifact",
    actionIndexes: [0, 1, 2, 3],
    promptIndexes: [0],
    showLinks: true,
    nextLabel: "Check the finished work",
  },
  {
    id: "artifact-checkpoint",
    title: "Make one change you can see, then download it",
    kind: "checkpoint",
    tone: "orange",
    stepId: "create-artifact",
    completesStep: true,
    nextLabel: "Step 9 is done",
  },
  {
    id: "step-save-workflow",
    title: "A useful answer is good. A reusable workflow is better",
    kind: "concept",
    tone: "dark",
    stepId: "save-workflow",
    concept: "project",
    showWhy: true,
    nextLabel: "Find the repeatable work",
  },
  {
    id: "calendar-skill-brainstorm",
    title: "Your calendar is full of workflow ideas",
    kind: "prompt",
    tone: "blue",
    stepId: "save-workflow",
    actionIndexes: [0, 1, 2, 3],
    promptIndexes: [0],
    nextLabel: "Pick one candidate",
  },
  {
    id: "project-setup",
    title: "Give the recurring work a home",
    kind: "activity",
    tone: "paper",
    stepId: "save-workflow",
    actionIndexes: [4, 5],
    showImage: true,
    nextLabel: "Add the instructions",
  },
  {
    id: "project-prompts",
    title: "Tell the Project how good work gets done",
    kind: "prompt",
    tone: "blue",
    stepId: "save-workflow",
    actionIndexes: [6],
    promptIndexes: [1, 2],
    nextLabel: "Run it once",
  },
  {
    id: "project-checkpoint",
    title: "Make every run teach the next one",
    kind: "checkpoint",
    tone: "dark",
    stepId: "save-workflow",
    actionIndexes: [7],
    showNote: true,
    showLinks: true,
    completesStep: true,
    nextLabel: "Finish the guide",
  },
  {
    id: "guide-complete",
    title: "You’re ready to put Claude to work",
    kind: "finish",
    tone: "dark",
  },
];

const VISIBLE_SLIDES = DECK_SLIDES.filter((slide) => slide.kind !== "checkpoint");

const TONE_CLASSES: Record<SlideTone, string> = {
  dark: "bg-[#020817] text-white",
  paper: "bg-[#faf8f5] text-slate-950",
  blue: "bg-[#0a1f44] text-white",
  orange: "bg-[#c8550a] text-white",
};

const STEP_ICONS = {
  "download-desktop": PlayCircle,
  "choose-model": BookOpen,
  "safe-defaults": ShieldCheck,
  "custom-instructions": Sparkles,
  "set-up-connectors": Link2,
  "how-might-we": MessageSquareText,
  "improve-answer": RotateCcw,
  "work-from-file": Paperclip,
  "create-artifact": FileCheck2,
  "save-workflow": FolderKanban,
} as const;

const howToSchema = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  name: "Claude Fundamentals: From Desktop Setup to a Reusable Workflow",
  description:
    "A free, non-technical slide guide for installing Claude Desktop, choosing a model, setting safe defaults, connecting live context, working from a file, creating an Artifact, finding an Agent Skill candidate, and saving a reusable workflow.",
  totalTime: "PT69M",
  estimatedCost: { "@type": "MonetaryAmount", currency: "USD", value: "0" },
  supply: [
    { "@type": "HowToSupply", name: "A free Claude account" },
    { "@type": "HowToSupply", name: "A Mac or Windows computer" },
    { "@type": "HowToSupply", name: "One low-risk practice file" },
    { "@type": "HowToSupply", name: "One example of a good finished result" },
  ],
  step: CLAUDE_BASICS_STEPS.map((step, index) => ({
    "@type": "HowToStep",
    position: index + 1,
    name: step.title,
    text: `${step.description} ${step.actions.join(" ")}`,
    url: `https://www.technical-leaders.com/claude-fundamentals#step-${step.id}`,
  })),
};

const copyText = async (value: string) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  textarea.remove();
};

const getStep = (stepId?: string) =>
  stepId ? CLAUDE_BASICS_STEPS.find((step) => step.id === stepId) : undefined;

const getStepNumber = (stepId?: string) =>
  stepId ? CLAUDE_BASICS_STEPS.findIndex((step) => step.id === stepId) + 1 : 0;

const getFirstSlideForStep = (stepId: string) =>
  VISIBLE_SLIDES.findIndex((slide) => slide.stepId === stepId);

const getSlideIndexFromHash = (hash: string) => {
  const requested = decodeURIComponent(hash.replace(/^#/, ""));
  if (!requested) return -1;

  const exactMatch = VISIBLE_SLIDES.findIndex((slide) => slide.id === requested);
  if (exactMatch >= 0) return exactMatch;

  const numericSlide = Number.parseInt(requested, 10);
  if (Number.isFinite(numericSlide) && numericSlide > 0) {
    return Math.min(numericSlide - 1, VISIBLE_SLIDES.length - 1);
  }

  return -1;
};

const PromptBlock = ({
  prompt,
  stepId,
  printMode = false,
}: {
  prompt: ClaudeBasicsPrompt;
  stepId: string;
  printMode?: boolean;
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await copyText(prompt.value);
      setCopied(true);
      trackEvent("Claude Fundamentals Prompt Copied", { step: stepId, prompt: prompt.label });
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-white/15 bg-[#020817] text-white shadow-2xl shadow-black/20">
      <div className="flex items-center justify-between gap-4 border-b border-white/10 px-4 py-3 sm:px-5">
        <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-orange-300">
          {prompt.label}
        </p>
        {!printMode && (
          <button
            type="button"
            onClick={handleCopy}
            className="inline-flex min-h-10 flex-shrink-0 items-center justify-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-bold text-slate-950 transition-colors hover:bg-orange-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#020817]"
          >
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            {copied ? "Copied" : "Copy prompt"}
          </button>
        )}
      </div>
      <pre className="max-h-[44svh] overflow-auto whitespace-pre-wrap px-4 py-5 font-mono text-[13px] leading-6 text-slate-100 sm:px-5 sm:text-sm lg:max-h-[46vh]">
        {prompt.value}
      </pre>
    </div>
  );
};

const ActionList = ({
  step,
  actionIndexes,
  light = false,
}: {
  step: ClaudeBasicsStep;
  actionIndexes?: number[];
  light?: boolean;
}) => {
  const actions = actionIndexes?.map((index) => ({ index, value: step.actions[index] })) ?? [];

  if (!actions.length) return null;

  return (
    <ol className="space-y-3 sm:space-y-4">
      {actions.map(({ index, value }) => (
        <li key={`${step.id}-${index}`} className="flex items-start gap-3 sm:gap-4">
          <span
            className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-sm font-extrabold ${
              light ? "bg-slate-950 text-white" : "bg-white/10 text-orange-300 ring-1 ring-white/15"
            }`}
          >
            {index + 1}
          </span>
          <span className={`pt-0.5 text-base leading-relaxed sm:text-lg ${light ? "text-slate-700" : "text-slate-200"}`}>
            {value}
          </span>
        </li>
      ))}
    </ol>
  );
};

const ScreenshotFrame = ({ step }: { step: ClaudeBasicsStep }) => {
  if (!step.image) return null;

  return (
    <figure className="overflow-hidden rounded-2xl border border-slate-950/10 bg-white shadow-2xl shadow-slate-950/15">
      <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-100 px-4 py-2.5" aria-hidden="true">
        <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
        <span className="ml-2 h-5 flex-1 rounded-md bg-white" />
      </div>
      <img src={step.image.src} alt={step.image.alt} className="h-auto w-full" />
      <figcaption className="border-t border-slate-200 bg-white px-4 py-3 text-sm leading-relaxed text-slate-600">
        {step.image.caption}
      </figcaption>
    </figure>
  );
};

const ReferenceTable = ({ step }: { step: ClaudeBasicsStep }) => {
  if (!step.table) return null;

  return (
    <div className="overflow-hidden rounded-2xl border border-white/15 bg-[#020817] text-white shadow-2xl shadow-black/20">
      <div className="border-b border-white/10 px-5 py-4">
        <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-orange-300">Which model should I use?</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] border-collapse text-left text-sm">
          <thead className="bg-white/[0.06] text-slate-200">
            <tr>
              {step.table.headers.map((header) => <th key={header} className="px-4 py-3 font-bold">{header}</th>)}
            </tr>
          </thead>
          <tbody>
            {step.table.rows.map((row, rowIndex) => (
              <tr key={row[0]} className={rowIndex === 0 ? "bg-orange-500/15" : "border-t border-white/10"}>
                {row.map((cell, cellIndex) => (
                  <td key={`${row[0]}-${cellIndex}`} className={`px-4 py-3 align-top leading-relaxed ${cellIndex === 0 ? "font-bold text-white" : "text-slate-300"}`}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ConceptVisual = ({ concept }: { concept?: ConceptType }) => {
  if (concept === "prompt-formula") {
    return (
      <div className="space-y-5">
        <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-white/15 bg-white/[0.06] p-5 text-xl font-semibold leading-relaxed sm:p-7 sm:text-2xl lg:text-3xl">
          <span>Help me</span>
          <span className="rounded-lg bg-orange-500 px-3 py-1 text-white">ACTION</span>
          <span>with</span>
          <span className="rounded-lg bg-sky-400 px-3 py-1 text-slate-950">CONTEXT</span>
          <span>so the result is</span>
          <span className="rounded-lg bg-emerald-400 px-3 py-1 text-slate-950">DONE</span>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {[
            ["01", "The ask", "What do you want Claude to do?"],
            ["02", "The context", "What does Claude need to know?"],
            ["03", "The finish line", "What does good look like?"],
            ["04", "A good example", "What should Claude learn from?"],
          ].map(([number, label, description]) => (
            <div key={label} className="rounded-xl border border-white/15 bg-white/[0.04] p-4">
              <span className="text-xs font-extrabold tracking-[0.15em] text-orange-300">{number}</span>
              <h3 className="mt-3 font-bold text-white">{label}</h3>
              <p className="mt-1 text-sm leading-relaxed text-slate-400">{description}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (concept === "feedback-loop") {
    return (
      <div className="grid gap-3 sm:grid-cols-5">
        {["Start with the draft", "Keep what worked", "Name the change", "Revise it", "Save the best one"].map((label, index) => (
          <div
            key={label}
            className={`relative rounded-2xl border p-4 text-center sm:min-h-28 sm:p-5 ${
              index === 4
                ? "border-orange-400 bg-orange-500 text-white"
                : "border-white/15 bg-white/[0.05] text-slate-100"
            }`}
          >
            <span className={`text-xs font-extrabold ${index === 4 ? "text-orange-100" : "text-orange-300"}`}>
              {String(index + 1).padStart(2, "0")}
            </span>
            <p className="mt-3 font-bold leading-snug">{label}</p>
            {index < 4 && (
              <ArrowRight className="absolute -right-3 top-1/2 z-10 hidden h-5 w-5 -translate-y-1/2 text-orange-400 sm:block" />
            )}
          </div>
        ))}
      </div>
    );
  }

  if (concept === "artifact") {
    return (
      <div className="grid items-stretch gap-4 sm:grid-cols-[1fr_auto_1fr] sm:gap-6">
        <div className="rounded-2xl border border-white/15 bg-white/[0.05] p-6">
          <MessageSquareText className="h-8 w-8 text-sky-300" />
          <p className="mt-5 text-xs font-extrabold uppercase tracking-[0.16em] text-sky-300">Chat</p>
          <h3 className="mt-2 font-serif text-3xl font-semibold">Where you give direction</h3>
          <p className="mt-3 text-slate-400">Ask questions, give feedback, and keep improving the work.</p>
        </div>
        <ArrowRight className="m-auto h-7 w-7 rotate-90 text-orange-400 sm:rotate-0" />
        <div className="rounded-2xl border border-orange-400 bg-orange-500 p-6 text-white shadow-xl shadow-orange-950/20">
          <FileCheck2 className="h-8 w-8" />
          <p className="mt-5 text-xs font-extrabold uppercase tracking-[0.16em] text-orange-100">Artifact</p>
          <h3 className="mt-2 font-serif text-3xl font-semibold">What you’ll actually use</h3>
          <p className="mt-3 text-orange-50">The finished document, checklist, table, or tool.</p>
        </div>
      </div>
    );
  }

  if (concept === "project") {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          ["Instructions", "How Claude should do the work"],
          ["Source material", "What the answer needs to use"],
          ["A good example", "What great work looks like"],
          ["Fresh input", "What changes on the next run"],
        ].map(([label, description], index) => (
          <div
            key={label}
            className={`rounded-2xl border p-5 ${
              index === 3 ? "border-orange-400 bg-orange-500" : "border-white/15 bg-white/[0.05]"
            }`}
          >
            <span className={`text-xs font-extrabold tracking-[0.15em] ${index === 3 ? "text-orange-100" : "text-orange-300"}`}>
              {String(index + 1).padStart(2, "0")}
            </span>
            <h3 className="mt-5 text-xl font-bold">{label}</h3>
            <p className={`mt-2 leading-relaxed ${index === 3 ? "text-orange-50" : "text-slate-400"}`}>{description}</p>
          </div>
        ))}
      </div>
    );
  }

  return null;
};

interface SlideContentProps {
  slide: DeckSlide;
  onStart: () => void;
  onGoToStep: (stepId: string) => void;
  printMode?: boolean;
}

const SlideContent = ({
  slide,
  onStart,
  onGoToStep,
  printMode = false,
}: SlideContentProps) => {
  const step = getStep(slide.stepId);
  const stepNumber = getStepNumber(slide.stepId);
  const light = slide.tone === "paper";
  const Icon = slide.stepId ? STEP_ICONS[slide.stepId as keyof typeof STEP_ICONS] ?? Sparkles : Sparkles;

  if (slide.kind === "cover") {
    return (
      <div className="relative mx-auto flex min-h-full w-full max-w-6xl items-center py-8 sm:py-12">
        <div className="grid w-full gap-10 lg:grid-cols-[1.12fr_0.88fr] lg:items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-orange-400/30 bg-orange-400/10 px-4 py-2 text-sm font-bold text-orange-300">
              <BookOpen className="h-4 w-4" />
              A practical, step-by-step guide
            </div>
            <h1 className="mt-7 max-w-4xl font-serif text-5xl font-semibold leading-[0.94] tracking-[-0.045em] text-white sm:text-6xl lg:text-7xl xl:text-8xl">
              Claude Fundamentals
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-300 sm:text-xl">
              You don’t need to learn every feature. Start with one real task. We’ll set up Claude, give it useful context, improve the first draft, and save a workflow you can run again.
            </p>
            <div className="mt-7 flex flex-wrap gap-2 text-sm font-semibold text-slate-200">
              {[`${CLAUDE_BASICS_STEPS.length} practical steps`, `${VISIBLE_SLIDES.length} slides`, "About 70 minutes", "No code required", "A free account is enough"].map((item) => (
                <span key={item} className="rounded-full border border-white/15 bg-white/[0.06] px-4 py-2">
                  {item}
                </span>
              ))}
            </div>
            {!printMode && (
              <button
                type="button"
                onClick={onStart}
                className="mt-8 inline-flex min-h-14 items-center justify-center gap-2 rounded-full bg-orange-500 px-7 text-base font-bold text-white transition-colors hover:bg-orange-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-300 focus-visible:ring-offset-2 focus-visible:ring-offset-[#020817] sm:text-lg"
              >
                Start or pick up where you left off
                <ArrowRight className="h-5 w-5" />
              </button>
            )}
          </div>

          <div className="rounded-3xl border border-white/15 bg-white/[0.07] p-6 shadow-2xl shadow-black/35 backdrop-blur sm:p-8">
            <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-orange-300">What you’ll leave with</p>
            <div className="mt-6 flex items-start gap-4">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-orange-500">
                <FolderKanban className="h-6 w-6" />
              </div>
              <div>
                <h2 className="font-serif text-3xl font-semibold tracking-[-0.03em]">One workflow you’ll actually use next week.</h2>
                <p className="mt-3 leading-relaxed text-slate-400">You’ll have the finished work, an example of what good looks like, and instructions Claude can follow again.</p>
              </div>
            </div>
            <div className="mt-7 h-2 overflow-hidden rounded-full bg-white/10">
              <div className="h-full w-[4%] rounded-full bg-gradient-to-r from-orange-500 to-amber-300" />
            </div>
            <p className="mt-3 text-sm text-slate-400">Your progress saves in this browser.</p>
          </div>
        </div>
      </div>
    );
  }

  if (slide.kind === "roadmap") {
    return (
      <div className="mx-auto flex min-h-full w-full max-w-6xl flex-col justify-center py-8 sm:py-12">
        <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-orange-300">{slide.eyebrow}</p>
        <h1 className="mt-4 max-w-4xl font-serif text-4xl font-semibold leading-tight tracking-[-0.04em] text-white sm:text-5xl lg:text-6xl">
          {slide.heading}
        </h1>
        <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {CLAUDE_BASICS_STEPS.map((item, index) => {
            const StepIcon = STEP_ICONS[item.id as keyof typeof STEP_ICONS] ?? Sparkles;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => onGoToStep(item.id)}
                disabled={printMode}
                className="group rounded-2xl border border-white/15 bg-white/[0.05] p-4 text-left transition-colors hover:border-orange-400/70 hover:bg-white/[0.09] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 disabled:cursor-default sm:p-5"
              >
                <div className="flex items-center justify-between">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 text-orange-300">
                    <StepIcon className="h-5 w-5" />
                  </span>
                  <span className="text-xs font-extrabold tracking-[0.16em] text-slate-500">{String(index + 1).padStart(2, "0")}</span>
                </div>
                <h2 className="mt-5 text-lg font-bold leading-snug text-white">{item.title}</h2>
                <p className="mt-2 text-sm text-slate-400">{item.duration}</p>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (!step) return null;

  if (slide.kind === "activity") {
    return (
      <div className="mx-auto flex min-h-full w-full max-w-6xl flex-col justify-center py-7 sm:py-10">
        <div className="flex items-center gap-3">
          <span className={`flex h-11 w-11 items-center justify-center rounded-2xl ${light ? "bg-slate-950 text-white" : "bg-orange-500 text-white"}`}>
            <Icon className="h-5 w-5" />
          </span>
          <div>
            <p className={`text-xs font-extrabold uppercase tracking-[0.17em] ${light ? "text-orange-700" : "text-orange-300"}`}>
              Step {stepNumber} of {CLAUDE_BASICS_STEPS.length} · {step.duration}
            </p>
            <p className={`mt-1 text-sm ${light ? "text-slate-500" : "text-slate-400"}`}>{step.description}</p>
          </div>
        </div>
        <h1 className="mt-5 max-w-4xl font-serif text-4xl font-semibold leading-tight tracking-[-0.04em] sm:text-5xl lg:text-6xl">
          {slide.heading ?? slide.title}
        </h1>

        {slide.showWhy && (
          <p className={`mt-4 max-w-4xl text-base leading-relaxed sm:text-lg ${light ? "text-slate-600" : "text-slate-300"}`}>
            {step.whyItMatters}
          </p>
        )}

        <div className={`mt-7 grid gap-7 ${slide.showImage || slide.showTable ? "lg:grid-cols-[0.82fr_1.18fr] lg:items-center" : "max-w-4xl"}`}>
          <div>
            <p className={`mb-4 text-xs font-extrabold uppercase tracking-[0.16em] ${light ? "text-slate-500" : "text-slate-400"}`}>Here’s what to do</p>
            <ActionList step={step} actionIndexes={slide.actionIndexes} light={light} />

            {slide.showNote && step.note && (
              <div className={`mt-6 rounded-2xl border-l-4 border-orange-500 p-4 text-sm leading-relaxed ${light ? "bg-orange-50 text-orange-950" : "bg-white/[0.06] text-slate-200"}`}>
                <strong>Keep in mind:</strong> {step.note}
              </div>
            )}

            {slide.showLinks && step.links && !printMode && (
              <div className="mt-6 flex flex-wrap gap-3">
                {step.links.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackClick("Claude Fundamentals Official Resource", { step: step.id, label: link.label })}
                    className={`inline-flex min-h-11 items-center gap-2 rounded-full px-5 py-2 text-sm font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 ${
                      light ? "bg-slate-950 text-white hover:bg-orange-600" : "bg-white text-slate-950 hover:bg-orange-50"
                    }`}
                  >
                    {link.label}
                    <ExternalLink className="h-4 w-4" />
                  </a>
                ))}
              </div>
            )}
          </div>
          {slide.showImage && <ScreenshotFrame step={step} />}
          {slide.showTable && <ReferenceTable step={step} />}
        </div>

      </div>
    );
  }

  if (slide.kind === "concept") {
    return (
      <div className="mx-auto flex min-h-full w-full max-w-6xl flex-col justify-center py-8 sm:py-12">
        <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-orange-300">Step {stepNumber} of {CLAUDE_BASICS_STEPS.length} · {step.duration}</p>
        <h1 className="mt-4 max-w-5xl font-serif text-4xl font-semibold leading-tight tracking-[-0.04em] text-white sm:text-5xl lg:text-6xl">
          {slide.heading ?? slide.title}
        </h1>
        {slide.showWhy && <p className="mt-4 max-w-4xl text-lg leading-relaxed text-slate-300">{step.whyItMatters}</p>}
        <div className="mt-8">
          <ConceptVisual concept={slide.concept} />
        </div>
        {slide.actionIndexes && (
          <div className="mt-7 max-w-4xl">
            <ActionList step={step} actionIndexes={slide.actionIndexes} />
          </div>
        )}
        {slide.showImage && step.image && (
          <div className="mt-7 max-w-3xl">
            <ScreenshotFrame step={step} />
          </div>
        )}
      </div>
    );
  }

  if (slide.kind === "prompt") {
    const prompts = slide.promptIndexes?.map((index) => step.prompts?.[index]).filter(Boolean) as ClaudeBasicsPrompt[];
    return (
      <div className="mx-auto flex min-h-full w-full max-w-6xl flex-col justify-center py-7 sm:py-10">
        <p className={`text-xs font-extrabold uppercase tracking-[0.18em] ${light ? "text-orange-700" : "text-orange-300"}`}>
          Step {stepNumber} of {CLAUDE_BASICS_STEPS.length} · Your turn
        </p>
        <h1 className="mt-4 max-w-5xl font-serif text-4xl font-semibold leading-tight tracking-[-0.04em] sm:text-5xl lg:text-6xl">
          {slide.heading ?? slide.title}
        </h1>
        <div className={`mt-7 grid gap-6 ${prompts.length > 1 ? "lg:grid-cols-2" : "lg:grid-cols-[0.72fr_1.28fr] lg:items-start"}`}>
          {prompts.length === 1 && (
            <div>
              <p className={`mb-4 text-xs font-extrabold uppercase tracking-[0.16em] ${light ? "text-slate-500" : "text-slate-400"}`}>Here’s what to do</p>
              <ActionList step={step} actionIndexes={slide.actionIndexes} light={light} />
              {slide.showNote && step.note && (
                <div className={`mt-6 rounded-2xl border-l-4 border-orange-500 p-4 text-sm leading-relaxed ${light ? "bg-orange-50 text-orange-950" : "bg-white/[0.06] text-slate-200"}`}>
                  <strong>Keep in mind:</strong> {step.note}
                </div>
              )}
            </div>
          )}
          {prompts.map((prompt) => (
            <PromptBlock key={prompt.label} prompt={prompt} stepId={step.id} printMode={printMode} />
          ))}
        </div>
        {prompts.length > 1 && (
          <div className="mt-6 max-w-4xl">
            <ActionList step={step} actionIndexes={slide.actionIndexes} />
          </div>
        )}
        {slide.showLinks && step.links && !printMode && (
          <div className="mt-6 flex flex-wrap gap-3">
            {step.links.map((link) => (
              <a
                key={link.href}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackClick("Claude Fundamentals Official Resource", { step: step.id, label: link.label })}
                className="inline-flex min-h-11 items-center gap-2 rounded-full border border-slate-300 bg-white px-5 py-2 text-sm font-bold text-slate-800 hover:border-orange-400 hover:text-orange-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
              >
                {link.label}
                <ExternalLink className="h-4 w-4" />
              </a>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (slide.kind === "finish") {
    const paths = [
      {
        eyebrow: "Put it to work",
        title: "Fortune 100 AI Prompt Library",
        body: "Sign up for free access, then pick a practical workflow and run the loop again.",
        href: "/library",
      },
      {
        eyebrow: "Learn the advanced tools",
        title: "Claude Masterclass",
        body: "Move into Chrome, Cowork, Claude Code, and reusable Skills.",
        href: "/claude-masterclass",
      },
      {
        eyebrow: "Give Claude more context",
        title: "AI Agent Starter",
        body: "Build on this with custom instructions, connectors, and a Chief of Staff workflow.",
        href: "/ai-agent-starter",
      },
      {
        eyebrow: "Bring it to your files",
        title: "The AI Coworker",
        body: "Set Claude up around the files and recurring work you already own.",
        href: "/ai-coworker",
      },
    ];

    return (
      <div className="mx-auto flex min-h-full w-full max-w-6xl flex-col justify-center py-8 sm:py-12">
        <div className="flex h-16 w-16 items-center justify-center rounded-3xl bg-emerald-400/15 text-emerald-300 ring-1 ring-emerald-300/30">
          <CheckCircle2 className="h-8 w-8" />
        </div>
        <p className="mt-6 text-xs font-extrabold uppercase tracking-[0.18em] text-orange-300">You did it</p>
        <h1 className="mt-4 max-w-5xl font-serif text-4xl font-semibold leading-tight tracking-[-0.04em] text-white sm:text-5xl lg:text-6xl">
          You’re ready to put Claude to work.
        </h1>
        <p className="mt-4 max-w-3xl text-lg leading-relaxed text-slate-300">
          Keep it simple: choose one workflow, bring the right context, show Claude what good looks like, review the result, and save what works.
        </p>
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {paths.map((path, index) => (
            <Link
              key={path.href}
              to={path.href}
              onClick={() => trackClick(index === 0 ? "Claude Fundamentals Next - Fortune 100 Prompt Library" : "Claude Fundamentals Graduation Path", { destination: path.href, completed: true })}
              className={`group rounded-2xl border p-5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 ${
                index === 0 ? "border-orange-400 bg-orange-500 text-white" : "border-white/15 bg-white/[0.05] text-white hover:border-orange-400/70 hover:bg-white/[0.09]"
              }`}
            >
              <p className={`text-xs font-extrabold uppercase tracking-[0.16em] ${index === 0 ? "text-orange-100" : "text-orange-300"}`}>{path.eyebrow}</p>
              <h2 className="mt-4 text-xl font-bold">{path.title}</h2>
              <p className={`mt-2 text-sm leading-relaxed ${index === 0 ? "text-orange-50" : "text-slate-400"}`}>{path.body}</p>
              <span className="mt-5 inline-flex items-center gap-2 text-sm font-bold">
                Keep learning
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
          ))}
        </div>
      </div>
    );
  }

  return null;
};

const ClaudeBasics = () => {
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [progressLoaded, setProgressLoaded] = useState(false);
  const [resumeSlideId, setResumeSlideId] = useState<string | null>(null);
  const [contentsOpen, setContentsOpen] = useState(false);
  const stageRef = useRef<HTMLElement>(null);
  const slideRef = useRef<HTMLElement>(null);
  const touchStart = useRef({ x: 0, y: 0 });

  const activeSlide = VISIBLE_SLIDES[activeSlideIndex];
  const slideProgress = Math.round(((activeSlideIndex + 1) / VISIBLE_SLIDES.length) * 100);
  const activeStepNumber = getStepNumber(activeSlide.stepId);

  useEffect(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      const savedSlide = localStorage.getItem(SLIDE_STORAGE_KEY);
      if (savedSlide && VISIBLE_SLIDES.some((slide) => slide.id === savedSlide)) setResumeSlideId(savedSlide);
    } catch {
      // The guide still works for the current visit when browser storage is blocked.
    }

    const hashIndex = getSlideIndexFromHash(window.location.hash);
    if (hashIndex >= 0) setActiveSlideIndex(hashIndex);

    setProgressLoaded(true);
    trackEvent("Claude Fundamentals Page View", {
      total_steps: CLAUDE_BASICS_STEPS.length,
      total_slides: VISIBLE_SLIDES.length,
    });
  }, []);

  useEffect(() => {
    const handleHashChange = () => {
      const hashIndex = getSlideIndexFromHash(window.location.hash);
      if (hashIndex < 0) return;

      setActiveSlideIndex(hashIndex);
      setContentsOpen(false);
      window.requestAnimationFrame(() => {
        slideRef.current?.scrollTo({ top: 0 });
        slideRef.current?.focus({ preventScroll: true });
      });
    };

    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  useEffect(() => {
    if (!progressLoaded) return;

    try {
      if (activeSlideIndex > 0) {
        localStorage.setItem(SLIDE_STORAGE_KEY, activeSlide.id);
        setResumeSlideId(activeSlide.id);
      }
    } catch {
      // Keep all behavior in memory when storage is unavailable.
    }

    if (window.location.hash !== `#${activeSlide.id}`) {
      window.history.replaceState(null, "", `#${activeSlide.id}`);
    }

    trackEvent("Claude Fundamentals Slide Viewed", {
      slide: activeSlide.id,
      slide_number: activeSlideIndex + 1,
      step: activeSlide.stepId ?? null,
      step_number: activeStepNumber || null,
    });
  }, [activeSlide.id, activeSlide.stepId, activeSlideIndex, activeStepNumber, progressLoaded]);

  const resumeTargetIndex = useMemo(() => {
    if (!resumeSlideId) return 1;

    if (resumeSlideId) {
      const savedIndex = VISIBLE_SLIDES.findIndex((slide) => slide.id === resumeSlideId);
      if (savedIndex > 0) return savedIndex;
    }
    return 1;
  }, [resumeSlideId]);

  const goToSlide = (nextIndex: number) => {
    const boundedIndex = Math.min(Math.max(nextIndex, 0), VISIBLE_SLIDES.length - 1);
    setActiveSlideIndex(boundedIndex);
    setContentsOpen(false);
    window.requestAnimationFrame(() => {
      slideRef.current?.scrollTo({ top: 0, behavior: "smooth" });
      slideRef.current?.focus({ preventScroll: true });
    });
  };

  const goToStep = (stepId: string) => {
    const targetIndex = getFirstSlideForStep(stepId);
    if (targetIndex >= 0) goToSlide(targetIndex);
  };

  const goNext = () => goToSlide(activeSlideIndex + 1);

  const startOrResume = () => {
    trackClick("Start or Resume Claude Fundamentals", {
      target_slide: VISIBLE_SLIDES[resumeTargetIndex]?.id,
    });
    goToSlide(resumeTargetIndex > 0 ? resumeTargetIndex : 1);
  };

  const resetProgress = () => {
    setResumeSlideId(null);
    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(SLIDE_STORAGE_KEY);
    } catch {
      // The in-memory reset still succeeds.
    }
    trackEvent("Claude Fundamentals Guide Restarted");
    goToSlide(0);
  };

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) await stageRef.current?.requestFullscreen();
      else await document.exitFullscreen();
    } catch {
      // Fullscreen may be blocked by the browser.
    }
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;

      if (event.key === "Escape") {
        setContentsOpen(false);
        return;
      }

      if (target?.closest("button, a, input, textarea, select, [role='dialog']")) return;

      if (contentsOpen) return;

      if (["ArrowRight", "PageDown", " "].includes(event.key)) {
        event.preventDefault();
        if (activeSlideIndex < VISIBLE_SLIDES.length - 1) goNext();
      } else if (["ArrowLeft", "PageUp"].includes(event.key)) {
        event.preventDefault();
        goToSlide(activeSlideIndex - 1);
      } else if (event.key === "Home") {
        event.preventDefault();
        goToSlide(0);
      } else if (event.key === "End") {
        event.preventDefault();
        goToSlide(VISIBLE_SLIDES.length - 1);
      } else if (event.key.toLowerCase() === "f") {
        void toggleFullscreen();
      } else if (event.key.toLowerCase() === "c" || event.key === "?") {
        setContentsOpen(true);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  });

  return (
    <div className="min-h-screen bg-black font-sans print:bg-white">
      <SEO
        title="Claude Fundamentals: A Step-by-Step Beginner Slide Guide"
        description="Learn Claude in ten interactive steps. Install Claude Desktop, choose a model, set safe defaults, add instructions and a connector, work from a file, create an Artifact, and save a reusable workflow."
        keywords={[
          "Claude fundamentals",
          "Claude for beginners",
          "how to use Claude",
          "Claude tutorial",
          "Claude Desktop",
          "Claude models",
          "Claude connectors",
          "Claude custom instructions",
          "Claude prompts",
          "Claude Artifacts",
          "Claude Projects",
          "AI training for non-technical people",
        ]}
        structuredData={howToSchema}
      />
      <style dangerouslySetInnerHTML={{ __html: `@media print {
        @page { size: 13.333in 7.5in; margin: 0; }
        html, body { margin: 0 !important; background: #fff !important; }
      }` }} />

      <main ref={stageRef} className="relative mx-auto flex h-[100svh] w-full max-w-[1920px] flex-col overflow-hidden bg-[#020817] text-white print:hidden">
        <header className="relative z-30 flex h-16 flex-shrink-0 items-center justify-between border-b border-white/10 bg-[#020817]/95 px-4 backdrop-blur sm:px-6 lg:px-8">
          <a
            href="/ai-30/program"
            className="inline-flex items-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400"
            aria-label="Back to the Longhand AI roadmap"
          >
            <img src="/orange-logo.png" alt="Technical Leaders" className="h-auto w-36 sm:w-44" />
          </a>

          <div className="flex items-center gap-2 sm:gap-3">
            {activeStepNumber > 0 && (
              <span className="hidden text-sm font-bold text-slate-300 sm:inline">Step {activeStepNumber} of {CLAUDE_BASICS_STEPS.length}</span>
            )}
            <span className="rounded-full border border-white/15 bg-white/[0.06] px-3 py-1.5 text-xs font-extrabold tracking-[0.08em] text-slate-200 sm:text-sm">
              {activeSlideIndex + 1} / {VISIBLE_SLIDES.length}
            </span>
            <button
              type="button"
              onClick={() => setContentsOpen(true)}
              className="inline-flex min-h-10 items-center gap-2 rounded-full border border-white/15 bg-white/[0.06] px-3 text-sm font-bold text-white transition-colors hover:border-orange-400 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 sm:px-4"
            >
              <Menu className="h-4 w-4" />
              <span className="hidden sm:inline">Contents</span>
            </button>
            <button
              type="button"
              onClick={() => window.print()}
              className="hidden h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/[0.06] text-white transition-colors hover:border-orange-400 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 md:inline-flex"
              aria-label="Print or save this guide as a PDF"
            >
              <Printer className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => void toggleFullscreen()}
              className="hidden h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/[0.06] text-white transition-colors hover:border-orange-400 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 md:inline-flex"
              aria-label="Toggle fullscreen"
            >
              <Maximize2 className="h-4 w-4" />
            </button>
          </div>
        </header>

        <section
          ref={slideRef}
          tabIndex={-1}
          aria-label={`${activeSlide.title}. Slide ${activeSlideIndex + 1} of ${VISIBLE_SLIDES.length}`}
          onTouchStart={(event) => {
            const touch = event.changedTouches[0];
            touchStart.current = { x: touch.clientX, y: touch.clientY };
          }}
          onTouchEnd={(event) => {
            const touch = event.changedTouches[0];
            const deltaX = touch.clientX - touchStart.current.x;
            const deltaY = touch.clientY - touchStart.current.y;
            if (Math.abs(deltaX) < 60 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
            if (deltaX < 0 && activeSlideIndex < VISIBLE_SLIDES.length - 1) goNext();
            if (deltaX > 0) goToSlide(activeSlideIndex - 1);
          }}
          className={`relative min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-5 outline-none sm:px-8 lg:px-12 ${TONE_CLASSES[activeSlide.tone]}`}
        >
          <div
            className="pointer-events-none absolute inset-0 opacity-50"
            aria-hidden="true"
            style={{
              backgroundImage:
                activeSlide.tone === "paper"
                  ? "radial-gradient(circle at 8% 16%, rgba(255,116,32,.10), transparent 24%), radial-gradient(circle at 88% 82%, rgba(10,31,68,.06), transparent 25%)"
                  : "radial-gradient(circle at 82% 18%, rgba(56,189,248,.12), transparent 26%), radial-gradient(circle at 10% 90%, rgba(255,116,32,.14), transparent 28%), linear-gradient(rgba(255,255,255,.025) 1px, transparent 1px), linear-gradient(90deg,rgba(255,255,255,.025) 1px,transparent 1px)",
              backgroundSize: activeSlide.tone === "paper" ? "auto" : "auto, auto, 48px 48px, 48px 48px",
            }}
          />
          <div key={activeSlide.id} className="relative min-h-full animate-in fade-in slide-in-from-bottom-2 duration-300">
            <SlideContent
              slide={activeSlide}
              onStart={startOrResume}
              onGoToStep={goToStep}
            />
          </div>
        </section>

        <footer className="relative z-30 flex min-h-[76px] flex-shrink-0 items-center gap-3 border-t border-white/10 bg-[#020817] px-4 sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={() => goToSlide(activeSlideIndex - 1)}
            disabled={activeSlideIndex === 0}
            className="inline-flex min-h-11 items-center gap-2 rounded-full border border-white/15 px-4 text-sm font-bold text-white transition-colors hover:border-orange-400 hover:bg-white/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 disabled:cursor-not-allowed disabled:opacity-30"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Back</span>
          </button>

          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-4 text-xs">
              <span className="truncate font-semibold text-slate-400">{activeSlide.title}</span>
              <span className="font-bold text-orange-300">{slideProgress}%</span>
            </div>
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/10" aria-hidden="true">
              <div
                className="h-full rounded-full bg-gradient-to-r from-orange-500 to-amber-300 transition-all duration-300"
                style={{ width: `${slideProgress}%` }}
              />
            </div>
          </div>

          {activeSlide.kind === "finish" ? (
            <a
              href="/ai-30/program"
              className="inline-flex min-h-11 items-center gap-2 rounded-full bg-white px-4 text-sm font-bold text-slate-950 transition-colors hover:bg-orange-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 sm:px-5"
            >
              <span className="hidden sm:inline">Back to roadmap</span>
              <span className="sm:hidden">Exit</span>
              <ArrowRight className="h-4 w-4" />
            </a>
          ) : (
            <button
              type="button"
              onClick={goNext}
              className="inline-flex min-h-11 items-center gap-2 rounded-full bg-orange-500 px-4 text-sm font-bold text-white transition-colors hover:bg-orange-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-300 sm:px-5"
            >
              <span className="max-w-[140px] truncate sm:max-w-none">{activeSlide.nextLabel ?? "Continue"}</span>
              <ArrowRight className="h-4 w-4 flex-shrink-0" />
            </button>
          )}
        </footer>

        {contentsOpen && (
          <div
            className="absolute inset-0 z-50 flex items-center justify-center bg-[#020817]/95 p-4 backdrop-blur-xl sm:p-8"
            role="dialog"
            aria-modal="true"
            aria-labelledby="contents-title"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) setContentsOpen(false);
            }}
          >
            <div className="flex max-h-[90svh] w-full max-w-5xl flex-col overflow-hidden rounded-3xl border border-white/15 bg-[#0a1f44] shadow-2xl shadow-black/50">
              <div className="flex items-start justify-between gap-4 border-b border-white/10 p-5 sm:p-7">
                <div>
                  <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-orange-300">Claude Fundamentals</p>
                  <h2 id="contents-title" className="mt-2 font-serif text-3xl font-semibold text-white sm:text-4xl">Your ten-step roadmap</h2>
                  <p className="mt-2 text-sm text-slate-400">Slide {activeSlideIndex + 1} of {VISIBLE_SLIDES.length}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setContentsOpen(false)}
                  className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full border border-white/15 text-white hover:border-orange-400 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400"
                  aria-label="Close contents"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="overflow-y-auto p-5 sm:p-7">
                <div className="grid gap-3 sm:grid-cols-2">
                  {CLAUDE_BASICS_STEPS.map((step, index) => {
                    const StepIcon = STEP_ICONS[step.id as keyof typeof STEP_ICONS] ?? Sparkles;
                    const active = activeSlide.stepId === step.id;
                    return (
                      <button
                        key={step.id}
                        type="button"
                        onClick={() => goToStep(step.id)}
                        className={`flex items-start gap-4 rounded-2xl border p-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 ${
                          active ? "border-orange-400 bg-orange-400/10" : "border-white/10 bg-white/[0.04] hover:border-white/25 hover:bg-white/[0.07]"
                        }`}
                      >
                        <span className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl ${active ? "bg-orange-500 text-white" : "bg-white/10 text-orange-300"}`}>
                          <StepIcon className="h-5 w-5" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="text-xs font-extrabold uppercase tracking-[0.14em] text-slate-500">Step {index + 1} · {step.duration}</span>
                          <span className="mt-1 block text-lg font-bold leading-snug text-white">{step.title}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-white/10 p-5 sm:px-7">
                <button
                  type="button"
                  onClick={resetProgress}
                  className="inline-flex min-h-10 items-center gap-2 text-sm font-semibold text-slate-400 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400"
                >
                  <RotateCcw className="h-4 w-4" />
                  Start over
                </button>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => window.print()}
                    className="inline-flex min-h-10 items-center gap-2 rounded-full border border-white/15 px-4 text-sm font-bold text-white hover:border-orange-400 hover:bg-white/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400"
                  >
                    <Printer className="h-4 w-4" />
                    Print / PDF
                  </button>
                  <button
                    type="button"
                    onClick={() => setContentsOpen(false)}
                    className="inline-flex min-h-10 items-center rounded-full bg-orange-500 px-5 text-sm font-bold text-white hover:bg-orange-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-300"
                  >
                    Continue
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      <div className="hidden print:block">
        {VISIBLE_SLIDES.map((slide) => (
          <article
            key={`print-${slide.id}`}
            className={`relative h-[7.5in] w-[13.333in] overflow-hidden px-[0.65in] [break-after:page] ${TONE_CLASSES[slide.tone]}`}
            style={{ WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }}
          >
            <SlideContent
              slide={slide}
              onStart={() => undefined}
              onGoToStep={() => undefined}
              printMode
            />
          </article>
        ))}
      </div>
    </div>
  );
};

export default ClaudeBasics;
